import React from "react";
import "./ReportPresentationFrame.css";
import PresentationInkLayer from "./PresentationInkLayer";

export default function ReportPresentationFrame({ children }) {
  const [presentationMode, setPresentationMode] = React.useState(false);
  const [highlights, setHighlights] = React.useState(false);
  const [penColor, setPenColor] = React.useState("#DA291C");
  const [eraser, setEraser] = React.useState(false);

  React.useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && presentationMode) setPresentationMode(false);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [presentationMode]);

  React.useEffect(() => {
    document.body.classList.toggle("report-presentation-mode", presentationMode);
    return () => document.body.classList.remove("report-presentation-mode");
  }, [presentationMode]);

  React.useEffect(() => {
    if (!presentationMode) {
      setHighlights(false);
      setEraser(false);
    }
  }, [presentationMode]);

  const togglePresentationMode = async () => {
    if (presentationMode) {
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
      setPresentationMode(false);
      return;
    }
    setPresentationMode(true);
    if (document.documentElement.requestFullscreen) {
      try { await document.documentElement.requestFullscreen(); } catch { /* CSS mode remains available */ }
    }
  };

  return (
    <div className={`report-presentation-frame${presentationMode ? " report-presentation-frame--active" : ""}`}>
      <button
        type="button"
        className={`report-presentation-frame__button${presentationMode ? " active" : ""}`}
        onClick={togglePresentationMode}
        aria-label={presentationMode ? "Exit report presentation mode" : "Open report presentation mode"}
        title={presentationMode ? "Exit presentation mode" : "Open presentation mode"}
      >
        <i className={`bi ${presentationMode ? "bi-fullscreen-exit" : "bi-fullscreen"}`} aria-hidden="true" />
        <span>{presentationMode ? "Exit" : "Present"}</span>
      </button>

      {presentationMode && (
        <div className="report-presentation-frame__tools" role="toolbar" aria-label="Report presentation controls">
          <button
            type="button"
            className={highlights ? "active" : ""}
            onClick={() => {
              setHighlights((current) => !current);
              setEraser(false);
            }}
            aria-pressed={highlights}
            aria-label={highlights ? "Disable presentation drawing" : "Enable presentation drawing"}
            title={highlights ? "Disable presentation drawing" : "Draw on the report"}
          >
            <i className="bi bi-pencil" aria-hidden="true" />
          </button>
          {highlights && (
            <>
              <label className="report-presentation-frame__color" title="Drawing color">
                <span className="report-presentation-frame__sr-only">Drawing color</span>
                <input type="color" value={penColor} onChange={(event) => setPenColor(event.target.value)} aria-label="Drawing color" />
              </label>
              <button
                type="button"
                className={eraser ? "active" : ""}
                onClick={() => setEraser((current) => !current)}
                aria-pressed={eraser}
                aria-label={eraser ? "Disable eraser" : "Enable eraser"}
                title={eraser ? "Disable eraser" : "Erase drawings"}
              >
                <i className="bi bi-eraser" aria-hidden="true" />
              </button>
            </>
          )}
          <button type="button" onClick={togglePresentationMode} aria-label="Exit report presentation mode" title="Exit presentation mode">
            <i className="bi bi-fullscreen-exit" aria-hidden="true" />
          </button>
        </div>
      )}

      <PresentationInkLayer active={presentationMode && highlights} color={penColor} eraser={eraser} />
      {children}
    </div>
  );
}
